import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
  faTiktok,
  faYelp,
  faGoogle,
} from '@fortawesome/free-brands-svg-icons';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Facebook' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Instagram' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const twitterLinkDef: SlotGroupDefinition = {
  label: 'Twitter / X',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_twitter_url',
      suggestedFields: ['property_twitter_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Twitter' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const youtubeLinkDef: SlotGroupDefinition = {
  label: 'YouTube',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_youtube_url',
      suggestedFields: ['property_youtube_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'YouTube' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const tiktokLinkDef: SlotGroupDefinition = {
  label: 'TikTok',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_tiktok_url',
      suggestedFields: ['property_tiktok_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'TikTok' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const yelpLinkDef: SlotGroupDefinition = {
  label: 'Yelp',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_yelp_url',
      suggestedFields: ['property_yelp_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Yelp' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const googleLinkDef: SlotGroupDefinition = {
  label: 'Google Business',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_business_url',
      suggestedFields: ['property_google_business_url'],
    },
    label: { label: 'Label', type: 'text', defaultValue: 'Google' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const linkClasses =
  'group flex flex-col items-center gap-3 p-6 transition-all duration-300';
const iconWrapClasses =
  'w-14 h-14 rounded-full border-2 border-[var(--vibe-color-primary)] flex items-center justify-center text-[var(--vibe-color-primary)] social-icon-wrap transition-all duration-300';
const labelClasses =
  'font-body text-sm uppercase tracking-wider text-[var(--vibe-color-text-muted)] group-hover:text-[var(--vibe-color-link-hover)] transition-colors duration-300';

export function SocialFollow() {
  const facebook = useSlotGroup('socialfollow.facebook_link', facebookLinkDef);
  const instagram = useSlotGroup('socialfollow.instagram_link', instagramLinkDef);
  const twitter = useSlotGroup('socialfollow.twitter_link', twitterLinkDef);
  const youtube = useSlotGroup('socialfollow.youtube_link', youtubeLinkDef);
  const tiktok = useSlotGroup('socialfollow.tiktok_link', tiktokLinkDef);
  const yelp = useSlotGroup('socialfollow.yelp_link', yelpLinkDef);
  const google = useSlotGroup('socialfollow.google_link', googleLinkDef);

  const hasAnySocial =
    facebook.url || instagram.url || twitter.url || youtube.url || tiktok.url || yelp.url || google.url;

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const iconsRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div ref={headingRef} className="reveal-fade-up">
          <RichTextContent
            path="socialfollow.title"
            defaultValue="Follow Us"
            as="h2"
            className="font-heading text-3xl sm:text-4xl font-normal text-[var(--vibe-color-text)] mb-4 uppercase tracking-wide social-title-gradient"
          />

          <div className="w-16 h-1 mx-auto mb-4 rounded-full social-accent-bar" />

          <RichTextContent
            path="socialfollow.subtitle"
            defaultValue="Stay connected with our community on social media."
            as="p"
            className="font-body text-lg text-[var(--vibe-color-text-muted)] max-w-2xl mx-auto mb-10 uppercase"
          />
        </div>

        {hasAnySocial ? (
          <div ref={iconsRef} className="reveal-fade-up flex flex-wrap justify-center gap-4 md:gap-6">
            {facebook.url && (
              <EditableSlotGroup groupPath="socialfollow.facebook_link" definition={facebookLinkDef}>
                <a href={facebook.url} target={facebook._target || '_blank'} rel="noopener noreferrer" aria-label="Facebook" className={linkClasses}>
                  <div className={iconWrapClasses}>
                    <FontAwesomeIcon icon={faFacebook} className="w-6 h-6" />
                  </div>
                  <span className={labelClasses}>{facebook.label || 'Facebook'}</span>
                </a>
              </EditableSlotGroup>
            )}

            {instagram.url && (
              <EditableSlotGroup groupPath="socialfollow.instagram_link" definition={instagramLinkDef}>
                <a href={instagram.url} target={instagram._target || '_blank'} rel="noopener noreferrer" aria-label="Instagram" className={linkClasses}>
                  <div className={iconWrapClasses}>
                    <FontAwesomeIcon icon={faInstagram} className="w-6 h-6" />
                  </div>
                  <span className={labelClasses}>{instagram.label || 'Instagram'}</span>
                </a>
              </EditableSlotGroup>
            )}

            {twitter.url && (
              <EditableSlotGroup groupPath="socialfollow.twitter_link" definition={twitterLinkDef}>
                <a href={twitter.url} target={twitter._target || '_blank'} rel="noopener noreferrer" aria-label="Twitter" className={linkClasses}>
                  <div className={iconWrapClasses}>
                    <FontAwesomeIcon icon={faXTwitter} className="w-6 h-6" />
                  </div>
                  <span className={labelClasses}>{twitter.label || 'Twitter'}</span>
                </a>
              </EditableSlotGroup>
            )}

            {youtube.url && (
              <EditableSlotGroup groupPath="socialfollow.youtube_link" definition={youtubeLinkDef}>
                <a href={youtube.url} target={youtube._target || '_blank'} rel="noopener noreferrer" aria-label="YouTube" className={linkClasses}>
                  <div className={iconWrapClasses}>
                    <FontAwesomeIcon icon={faYoutube} className="w-6 h-6" />
                  </div>
                  <span className={labelClasses}>{youtube.label || 'YouTube'}</span>
                </a>
              </EditableSlotGroup>
            )}

            {tiktok.url && (
              <EditableSlotGroup groupPath="socialfollow.tiktok_link" definition={tiktokLinkDef}>
                <a href={tiktok.url} target={tiktok._target || '_blank'} rel="noopener noreferrer" aria-label="TikTok" className={linkClasses}>
                  <div className={iconWrapClasses}>
                    <FontAwesomeIcon icon={faTiktok} className="w-6 h-6" />
                  </div>
                  <span className={labelClasses}>{tiktok.label || 'TikTok'}</span>
                </a>
              </EditableSlotGroup>
            )}

            {yelp.url && (
              <EditableSlotGroup groupPath="socialfollow.yelp_link" definition={yelpLinkDef}>
                <a href={yelp.url} target={yelp._target || '_blank'} rel="noopener noreferrer" aria-label="Yelp" className={linkClasses}>
                  <div className={iconWrapClasses}>
                    <FontAwesomeIcon icon={faYelp} className="w-6 h-6" />
                  </div>
                  <span className={labelClasses}>{yelp.label || 'Yelp'}</span>
                </a>
              </EditableSlotGroup>
            )}

            {google.url && (
              <EditableSlotGroup groupPath="socialfollow.google_link" definition={googleLinkDef}>
                <a href={google.url} target={google._target || '_blank'} rel="noopener noreferrer" aria-label="Google" className={linkClasses}>
                  <div className={iconWrapClasses}>
                    <FontAwesomeIcon icon={faGoogle} className="w-6 h-6" />
                  </div>
                  <span className={labelClasses}>{google.label || 'Google'}</span>
                </a>
              </EditableSlotGroup>
            )}
          </div>
        ) : (
          <p className="font-body text-[var(--vibe-color-text-muted)]">
            Social media links will appear here.
          </p>
        )}
      </div>

      <style>{`
        .social-title-gradient {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .social-accent-bar {
          background: linear-gradient(90deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 75%, black) 100%);
        }
        .group:hover .social-icon-wrap {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border-color: transparent;
          color: var(--vibe-color-text-on-primary);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SocialFollow',
  displayName: 'Social Follow',
  tokens: {
    'socialfollow.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'Follow Us',
    },
    'socialfollow.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: 'Stay connected with our community on social media.',
    },
  },
  slotGroups: {
    'socialfollow.facebook_link': facebookLinkDef,
    'socialfollow.instagram_link': instagramLinkDef,
    'socialfollow.twitter_link': twitterLinkDef,
    'socialfollow.youtube_link': youtubeLinkDef,
    'socialfollow.tiktok_link': tiktokLinkDef,
    'socialfollow.yelp_link': yelpLinkDef,
    'socialfollow.google_link': googleLinkDef,
  },
};

(SocialFollow as any).contentSchema = contentSchema;

export default SocialFollow;
